import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SinglePage from "../SinglePage/SinglePage";
import SubmitButton from "../../base/Buttons/SubmitButton/SubmitButton";
import ConfirmationDialog from "../../base/ConfirmationDialog";
import ShopNavbar from "./ShopNavbar";
import { loadAuthSession } from "../../../services/authStorage";
import { createShopReservation } from "../../../services/shopApi";
import {
  CartPageCard,
  CartPageContainer,
  CartPageEmpty,
  CartPageHeader,
  CartPageHint,
  CartPageList,
  CartPageRow,
  CartPageRowActions,
  CartPageRowInfo,
  CartPageRowName,
  CartPageRowOptions,
  CartPageRowPrice,
  CartPageSummary,
  CartPageTitle,
  QuantityButton,
  QuantityRow,
  QuantityValue,
  RemoveButton
} from "./shopCartReviewStyles";

interface CartEntry {
  key: string;
  productSlug: string;
  productName: string;
  price: number;
  quantity: number;
  selectedOptions: Record<string, string>;
}

const CART_STORAGE_KEY = "shop-cart-entries";

const formatPrice = (value: number): string =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(value);

const formatOptions = (options: Record<string, string>): string => {
  const entries = Object.entries(options);
  if (entries.length === 0) {
    return "Standard";
  }

  return entries.map(([name, value]) => `${name}: ${value}`).join(" | ");
};

const ShopCartReview = () => {
  const navigate = useNavigate();
  const [cartEntries, setCartEntries] = useState<CartEntry[]>(() => {
    try {
      const raw = localStorage.getItem(CART_STORAGE_KEY);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? (parsed as CartEntry[]).map((entry) => ({
            ...entry,
            productSlug: entry.productSlug ?? entry.key.split("|")[0] ?? "",
          }))
        : [];
    } catch {
      return [];
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartEntries));
  }, [cartEntries]);

  const cartItemsCount = useMemo(
    () => cartEntries.reduce((sum, entry) => sum + entry.quantity, 0),
    [cartEntries]
  );
  const subtotal = useMemo(
    () => cartEntries.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [cartEntries]
  );

  const updateCartQuantity = (key: string, direction: "up" | "down") => {
    setCartEntries((prev) =>
      prev.flatMap((entry) => {
        if (entry.key !== key) {
          return [entry];
        }

        if (direction === "down" && entry.quantity === 1) {
          return [];
        }

        const nextQuantity = direction === "up" ? entry.quantity + 1 : entry.quantity - 1;
        return [{ ...entry, quantity: nextQuantity }];
      })
    );
  };

  const removeEntry = (key: string) => {
    setCartEntries((prev) => prev.filter((entry) => entry.key !== key));
  };

  const openCheckoutConfirmation = () => {
    const session = loadAuthSession();
    if (!session) {
      navigate("/konto");
      return;
    }

    const validEntries = cartEntries.filter((entry) => entry.productSlug);
    if (validEntries.length === 0) {
      setSubmitError("Der Warenkorb enthält keine gültigen Artikel für eine Reservierung.");
      return;
    }

    setSubmitError("");
    setSubmitSuccess("");
    setIsConfirmationOpen(true);
  };

  const handleCheckout = async () => {
    const session = loadAuthSession();
    if (!session) {
      setIsConfirmationOpen(false);
      navigate("/konto");
      return;
    }

    const validEntries = cartEntries.filter((entry) => entry.productSlug);
    if (validEntries.length === 0) {
      setIsConfirmationOpen(false);
      setSubmitError("Der Warenkorb enthält keine gültigen Artikel für eine Reservierung.");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    setSubmitSuccess("");

    try {
      const reservation = await createShopReservation(
        session.token,
        validEntries.map((entry) => ({
          productSlug: entry.productSlug,
          quantity: entry.quantity,
          selectedOptions: entry.selectedOptions,
        }))
      );

      setCartEntries([]);
      setIsConfirmationOpen(false);
      setSubmitSuccess(`Reservierung ${reservation.id.slice(0, 8)} wurde erfolgreich angelegt.`);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Reservierung konnte nicht angelegt werden.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SinglePage
      title="Reservierung"
      heroBanner={<></>}
      topic={null}
      content={
        <CartPageContainer>
          <ShopNavbar cartItemsCount={cartItemsCount} onCartClick={() => navigate("/shop/warenkorb")} />

          <CartPageCard>
            <CartPageHeader>
              <CartPageTitle>Reservierung prüfen</CartPageTitle>
              <CartPageHint>
                Prüfe hier alle Artikel und sende danach eine unverbindliche Reservierung ab. Es wird
                keine Reservierung mit Fernabsatzvertrag abgeschlossen.
              </CartPageHint>
            </CartPageHeader>

            {cartEntries.length === 0 ? (
              <CartPageEmpty>Dein Warenkorb ist aktuell leer.</CartPageEmpty>
            ) : (
              <CartPageList>
                {cartEntries.map((entry) => (
                  <CartPageRow key={entry.key}>
                    <CartPageRowInfo>
                      <CartPageRowName>{entry.productName}</CartPageRowName>
                      <CartPageRowOptions>{formatOptions(entry.selectedOptions)}</CartPageRowOptions>
                    </CartPageRowInfo>

                    <CartPageRowActions>
                      <QuantityRow>
                        <QuantityButton type="button" onClick={() => updateCartQuantity(entry.key, "down")}>
                          -
                        </QuantityButton>
                        <QuantityValue>{entry.quantity}</QuantityValue>
                        <QuantityButton type="button" onClick={() => updateCartQuantity(entry.key, "up")}>
                          +
                        </QuantityButton>
                      </QuantityRow>
                      <CartPageRowPrice>{formatPrice(entry.price * entry.quantity)}</CartPageRowPrice>
                      <RemoveButton type="button" onClick={() => removeEntry(entry.key)}>
                        Entfernen
                      </RemoveButton>
                    </CartPageRowActions>
                  </CartPageRow>
                ))}
              </CartPageList>
            )}

            <CartPageSummary>
              <div>Positionen: {cartItemsCount}</div>
              <div>Gesamt: {formatPrice(subtotal)}</div>
            </CartPageSummary>

            <CartPageHint>
              Mit dem Abschluss wird eine Reservierung für den Shopmanager angelegt. Die weitere
              Abstimmung erfolgt anschließend direkt über den Verein.
            </CartPageHint>
            {submitError ? <CartPageHint style={{ color: "#ffb4b4" }}>{submitError}</CartPageHint> : null}
            {submitSuccess ? <CartPageHint style={{ color: "#b9ee9d" }}>{submitSuccess}</CartPageHint> : null}

            <CartPageHeader>
              <SubmitButton text="Weiter einkaufen" onClick={() => navigate("/shop")} />
              <SubmitButton
                text={isSubmitting ? "Reservierung wird gesendet..." : "Reservierung absenden"}
                onClick={openCheckoutConfirmation}
                disabled={isSubmitting || cartEntries.length === 0}
              />
            </CartPageHeader>
          </CartPageCard>

          <ConfirmationDialog
            open={isConfirmationOpen}
            onClose={() => setIsConfirmationOpen(false)}
            onConfirm={handleCheckout}
            isConfirming={isSubmitting}
            title="Reservierung absenden?"
            confirmText="Reservierung absenden"
            description={
              <>
                <CartPageHint>
                  Du sendest {cartItemsCount} Position{cartItemsCount === 1 ? "" : "en"} mit einem Gesamtwert von {formatPrice(subtotal)}
                  {' '}als unverbindliche Reservierungsanfrage an den Shopmanager.
                </CartPageHint>
                <CartPageHint style={{ marginTop: "0.75rem" }}>
                  Danach wird dein Warenkorb geleert. Die weitere Abstimmung erfolgt direkt über den Verein.
                </CartPageHint>
              </>
            }
          />
        </CartPageContainer>
      }
    />
  );
};

export default ShopCartReview;



